import {useSearchParams} from 'react-router-dom';
import {IndicatorsByMemberListOrdering} from 'api/v2';
import {NEVER_ACTIVATED_SCOPE, NO_FIRST_ACTION_SCOPE} from './queries';
import type {FeedbackFilter} from './types';

export type AccessScope =
  typeof NEVER_ACTIVATED_SCOPE | typeof NO_FIRST_ACTION_SCOPE;

export type SignalFilters = {
  reviewFilter: FeedbackFilter;
  program: string;
  kinds: string[];
  scope: AccessScope | undefined;
  ordering: IndicatorsByMemberListOrdering;
};

const DEFAULT_ORDERING = IndicatorsByMemberListOrdering['-computedAt'];
const REVIEW_VALUES: FeedbackFilter[] = ['pending', 'reviewed'];
const SCOPE_VALUES: AccessScope[] = [
  NEVER_ACTIVATED_SCOPE,
  NO_FIRST_ACTION_SCOPE,
];
const ORDERING_VALUES = Object.values(IndicatorsByMemberListOrdering);

// Params this hook owns; everything at its default value is omitted from the URL.
const PARAM_KEYS = ['review', 'program', 'kinds', 'scope', 'sort'] as const;

function parse(params: URLSearchParams): SignalFilters {
  const review = params.get('review');
  const scope = params.get('scope');
  const sort = params.get('sort');
  return {
    reviewFilter: REVIEW_VALUES.includes(review as FeedbackFilter)
      ? (review as FeedbackFilter)
      : 'all',
    program: params.get('program') ?? 'all',
    kinds: params.get('kinds')?.split(',').filter(Boolean) ?? [],
    scope: SCOPE_VALUES.includes(scope as AccessScope)
      ? (scope as AccessScope)
      : undefined,
    ordering: ORDERING_VALUES.includes(sort as IndicatorsByMemberListOrdering)
      ? (sort as IndicatorsByMemberListOrdering)
      : DEFAULT_ORDERING,
  };
}

// Signal-list filter + sort state, backed by the URL query string so it's
// shareable, bookmarkable and survives a refresh.
export function useSignalFilters(): {
  filters: SignalFilters;
  setFilters: (changes: Partial<SignalFilters>) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = parse(searchParams);

  const setFilters = (changes: Partial<SignalFilters>) => {
    const next = {...filters, ...changes};
    const params = new URLSearchParams(searchParams);
    PARAM_KEYS.forEach((key) => params.delete(key));
    if (next.reviewFilter !== 'all') params.set('review', next.reviewFilter);
    if (next.program !== 'all') params.set('program', next.program);
    if (next.kinds.length > 0) params.set('kinds', next.kinds.join(','));
    if (next.scope) params.set('scope', next.scope);
    if (next.ordering !== DEFAULT_ORDERING) params.set('sort', next.ordering);
    setSearchParams(params, {replace: true});
  };

  return {filters, setFilters};
}
